import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Segmented, Spin, Tag, Timeline, message } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  PhoneOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { callCenterCallsApi } from '../../utils/api';
import { toJalali } from '../../utils/dateUtils';
import CallSubjectChip from '../call-center/subjects/CallSubjectChip';

const PER_PAGE = 20;

const DIRECTION_META = {
  incoming: { label: 'تماس ورودی', color: 'blue', dot: '#1677ff', icon: <ArrowDownOutlined /> },
  outgoing: { label: 'تماس خروجی', color: 'green', dot: '#52c41a', icon: <ArrowUpOutlined /> },
  missed: { label: 'تماس از دست رفته', color: 'red', dot: '#ff4d4f', icon: <PhoneOutlined /> },
};

const FILTER_OPTIONS = [
  { value: 'all', label: 'همه' },
  { value: 'incoming', label: 'ورودی' },
  { value: 'outgoing', label: 'خروجی' },
  { value: 'missed', label: 'از دست رفته' },
];

const DISPOSITION_LABELS = {
  ANSWERED: 'پاسخ داده',
  'NO ANSWER': 'بی‌پاسخ',
  NOANSWER: 'بی‌پاسخ',
  BUSY: 'مشغول',
};

const formatDuration = (seconds) => {
  const s = Number(seconds) || 0;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const unwrapList = (res, fallbackPage) => {
  const raw = res?.data ?? res ?? {};
  if (Array.isArray(raw)) return { list: raw, total: raw.length, page: fallbackPage };
  const list = Array.isArray(raw.data) ? raw.data : Array.isArray(raw.items) ? raw.items : [];
  return {
    list,
    total: Number(raw.total ?? list.length),
    page: Number(raw.current_page ?? raw.page ?? fallbackPage),
  };
};

/**
 * تایم‌لاین تماس‌های مشتری (ورودی، خروجی، از دست رفته) به تفکیک روز
 */
const ClientCallsTimeline = ({ clientId, onOpenCall }) => {
  const [direction, setDirection] = useState('all');
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchPage = useCallback(
    async (targetPage, append) => {
      if (!clientId) return;
      setLoading(true);
      try {
        const res = await callCenterCallsApi.getAll({
          page: targetPage,
          per_page: PER_PAGE,
          client_user_id: clientId,
          direction,
        });
        const { list, total: totalCount, page: currentPage } = unwrapList(res, targetPage);
        setCalls((prev) => (append ? [...prev, ...list] : list));
        setTotal(totalCount);
        setPage(currentPage);
      } catch (error) {
        message.error(error?.message || 'خطا در دریافت تماس‌های مشتری');
      } finally {
        setLoading(false);
      }
    },
    [clientId, direction],
  );

  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  const groups = useMemo(() => {
    const byDay = new Map();
    calls.forEach((call) => {
      const day = call.started_at ? toJalali(call.started_at, 'jYYYY/jMM/jDD') : 'نامشخص';
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push(call);
    });
    return Array.from(byDay.entries());
  }, [calls]);

  const renderCall = (call) => {
    const meta = DIRECTION_META[call.direction] || DIRECTION_META.incoming;
    const time = call.started_at ? toJalali(call.started_at, 'HH:mm') : '—';
    const answered = Number(call.duration) > 0;
    const disposition = DISPOSITION_LABELS[String(call.disposition || '').toUpperCase()];

    return {
      key: call.id,
      color: meta.dot,
      dot: undefined,
      children: (
        <div
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 cursor-pointer hover:shadow-sm transition-shadow"
          onClick={() => onOpenCall?.(call)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onOpenCall?.(call)}
        >
          <div className="flex items-center flex-wrap gap-2">
            <span className="custom-font font-semibold text-gray-800 dark:text-gray-100" dir="ltr">
              {time}
            </span>
            <Tag color={meta.color} icon={meta.icon} className="m-0">
              {meta.label}
            </Tag>
            {answered ? (
              <span className="custom-font text-gray-600 dark:text-gray-300">
                — <span dir="ltr">{formatDuration(call.duration)}</span> دقیقه
              </span>
            ) : disposition ? (
              <Tag className="m-0">{disposition}</Tag>
            ) : null}
            {call.high_risk ? <Tag color="red" className="m-0">پرریسک</Tag> : null}
            {call.missed_opportunity ? <Tag color="volcano" className="m-0">فرصت فروش ازدست‌رفته</Tag> : null}
            {call.subject ? <CallSubjectChip subject={call.subject} /> : null}
            <RightOutlined className="ms-auto text-gray-400 rotate-180" />
          </div>
          <div className="mt-1 flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 custom-font">
            {call.agent?.name ? <span>اپراتور: {call.agent.name}</span> : null}
            {call.phone ? <span dir="ltr">{call.phone}</span> : null}
            {call.deal?.name ? <span>معامله: {call.deal.name}</span> : null}
          </div>
          {call.summary ? (
            <p className="mt-2 mb-0 text-sm text-gray-600 dark:text-gray-300 custom-font line-clamp-2">
              {call.summary}
            </p>
          ) : null}
        </div>
      ),
    };
  };

  return (
    <div className="pt-2 custom-font">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <Segmented options={FILTER_OPTIONS} value={direction} onChange={setDirection} />
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {total.toLocaleString('fa-IR')} تماس
        </span>
      </div>

      <Spin spinning={loading && calls.length === 0}>
        {groups.length === 0 && !loading ? (
          <Empty description="تماسی برای این مشتری ثبت نشده است" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          groups.map(([day, items]) => (
            <div key={day} className="mb-6">
              <div className="mb-3 text-sm font-semibold text-gray-500 dark:text-gray-400" dir="ltr" style={{ textAlign: 'right' }}>
                {day}
              </div>
              <Timeline items={items.map(renderCall)} />
            </div>
          ))
        )}
      </Spin>

      {calls.length < total ? (
        <div className="text-center mt-2">
          <Button loading={loading} onClick={() => fetchPage(page + 1, true)}>
            نمایش بیشتر
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default ClientCallsTimeline;
